import type {
  PmeDocusignEnvelopeKind,
  PmeDocusignSignaturesResponse,
} from '@/services/proposals/types';
import {
  groupLifeProposalsService,
  type InitiatePmeDocusignSignaturesPayload,
} from '@/services/proposals/group-life';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const pmeDocusignSignaturesQueryKey = (policyId: string) =>
  ['pme-docusign-signatures', policyId] as const;

export function usePmeDocusignSignaturesStatus(
  policyId: string,
  enabled = true
) {
  const trimmed = policyId.trim();

  return useQuery({
    queryKey: pmeDocusignSignaturesQueryKey(trimmed),
    enabled: Boolean(trimmed) && enabled,
    retry: false,
    queryFn: async (): Promise<PmeDocusignSignaturesResponse | null> => {
      const response =
        await groupLifeProposalsService.getDocusignSignaturesStatus(trimmed);

      if (response.statusCode === 404) {
        return null;
      }

      if (!response.success) {
        throw new Error(
          response.error || 'Erro ao carregar assinaturas digitais'
        );
      }

      return response.data ?? null;
    },
  });
}

export function useInitiatePmeDocusignSignatures(policyId: string) {
  const queryClient = useQueryClient();
  const trimmed = policyId.trim();

  return useMutation({
    mutationFn: async (payload: InitiatePmeDocusignSignaturesPayload) => {
      const response =
        await groupLifeProposalsService.initiateDocusignSignatures({
          policyId: trimmed,
          payload,
        });

      if (!response.success || !response.data) {
        throw Object.assign(
          new Error(response.error || 'Erro ao iniciar assinaturas digitais'),
          { statusCode: response.statusCode }
        );
      }

      return response.data;
    },
    onSuccess: data => {
      queryClient.setQueryData(pmeDocusignSignaturesQueryKey(trimmed), data);
    },
    // O backend sobe a proposta para "Em assinatura" (12) ao criar o envelope,
    // ou para "Em análise" (18) quando a subscrição segura o Confirmar. Sem
    // recarregar o snapshot, o resumo continuaria exibindo o status antigo e
    // editável. `onSettled` porque um erro parcial também pode ter mudado o
    // status no InsureMO.
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['group-life-proposal', trimmed],
      });
      await queryClient.invalidateQueries({ queryKey: ['proposals'] });
    },
  });
}

export function useSyncPmeDocusignSignatures(policyId: string) {
  const queryClient = useQueryClient();
  const trimmed = policyId.trim();

  return useMutation({
    mutationFn: async (kind?: string) => {
      const response = await groupLifeProposalsService.syncDocusignSignatures(
        trimmed,
        kind
      );

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao atualizar status');
      }

      return response.data;
    },
    onSuccess: async data => {
      queryClient.setQueryData(pmeDocusignSignaturesQueryKey(trimmed), data);

      // Com tudo assinado o sync já transmite: o status da proposta mudou para
      // "Proposta Transmitida" (8) e o snapshot em cache ficou para trás.
      const status = data.transmission?.status;
      if (status === 'transmitted' || status === 'already_transmitted') {
        await queryClient.invalidateQueries({
          queryKey: ['group-life-proposal', trimmed],
        });
        await queryClient.invalidateQueries({ queryKey: ['proposals'] });
      }
    },
  });
}

export function useRetryPmeDocusignSignature(policyId: string) {
  const queryClient = useQueryClient();
  const trimmed = policyId.trim();

  return useMutation({
    mutationFn: async (envelopeKind: PmeDocusignEnvelopeKind) => {
      const response = await groupLifeProposalsService.retryDocusignSignature(
        trimmed,
        envelopeKind
      );

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao retentar assinatura');
      }

      return response.data;
    },
    onSuccess: data => {
      queryClient.setQueryData(pmeDocusignSignaturesQueryKey(trimmed), data);
    },
  });
}
